import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Clock, User } from 'lucide-react';
import { API } from '@/App';

const ArticleDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'article');
      navigate('/articles');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading">
        <div className="animate-pulse text-[#1e3a5f]">Chargement...</div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50" data-testid="article-detail-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/articles')}
            className="mb-4 text-gray-600 hover:text-gray-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour aux articles
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <span className="px-4 py-2 bg-green-50 text-green-700 rounded-full font-medium" data-testid="category">
            {article.category}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-testid="title">
          {article.title}
        </h1>

        <div className="flex items-center gap-6 text-gray-600 mb-8">
          <div className="flex items-center">
            <User className="mr-2" size={20} />
            <span data-testid="author">{article.author}</span>
          </div>
          <div className="flex items-center">
            <Clock className="mr-2" size={20} />
            <span data-testid="created-date">
              {new Date(article.created_at).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>

        <Card className="border-2 border-gray-100">
          <CardContent className="p-8">
            <div className="prose max-w-none text-gray-700 leading-relaxed" data-testid="content">
              {article.content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ArticleDetailPage;
